import React, { useMemo } from 'react';

import { useRouter, usePathname } from 'next/navigation';

import { Box, Button, useTheme as useMuiTheme } from '@mui/material';

import { useUserInfo } from '@/context/UserInfoContext';
import { useOmniBoxWidth } from '@/hooks/useOmniBoxWidth';
import { useTheme } from '@/theme/ThemeContext';
import { COLOR_MAPPING, resolveColors } from '@/theme/colorPalettes';
import { createElevationStyles } from '@/theme/elevationStyles';

export const SignedOutAuthCard: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();
  const muiTheme = useMuiTheme();
  const { mode } = useTheme();
  const elevationStyles = createElevationStyles(muiTheme, 4);
  const { width, maxWidth, minWidth } = useOmniBoxWidth();
  const { userInfo } = useUserInfo();

  // Resolve colors for floating widgets
  const colors = useMemo(() => {
    const resolved = resolveColors(COLOR_MAPPING, mode);
    return {
      bg: resolved.floatingWidgetBg,
      fg: resolved.floatingWidgetFg,
    };
  }, [mode]);

  // Don't show on login page, register pages, or if user is logged in
  if (pathname === '/login' || pathname.startsWith('/register') || userInfo) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        width: width,
        maxWidth: maxWidth,
        minWidth: minWidth,
        zIndex: muiTheme.zIndex.appBar,
      }}
    >
      <Box
        sx={{
          height: 64,
          borderRadius: 24,
          backgroundColor: colors.bg,
          color: colors.fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          gap: 2,
          ...elevationStyles,
          boxShadow: muiTheme.shadows[8],
          border: `1px solid ${muiTheme.palette.divider}`,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={() => router.push('/login')}
        >
          Log in
        </Button>

        {/* uncomment when public signup is available
         <Button
          variant="outlined"
          size="small"
          onClick={() => router.push('/register')}
          sx={{
            borderColor: colors.fg,
            color: colors.fg,
            textTransform: 'none',
            '&:hover': {
              borderColor: colors.fg,
              backgroundColor: `${colors.fg}10`,
            },
          }}
        >
          Sign up
        </Button> */}
      </Box>
    </Box>
  );
};
